import { Injectable, inject } from '@angular/core';
import { PdfService } from './pdf.service';
import { ReferenceDisplayService } from './reference-display.service';
import { firstValueFrom } from 'rxjs';

export interface SpecimenContainerLabelPrintData {
  patientPrimaryId: string;
  patientName?: string;
  patientFallbackId?: string;
  serviceRequestId?: string;
  serviceRequestName?: string;
  specimenType?: string;
  receivedTime?: string;
  containerId?: string;
}

@Injectable({ providedIn: 'root' })
export class SpecimenContainerLabelPrintService {
  private readonly pdf = inject(PdfService);

  private readonly refDisplay = inject(ReferenceDisplayService);

  private mmToPt(mm: number): number {
    // 1 inch = 25.4mm, 1 point = 1/72 inch
    return (mm / 25.4) * 72;
  }

  async printSmallLabel(data: SpecimenContainerLabelPrintData): Promise<void> {
    const patientPrimaryIdRaw = (data.patientPrimaryId ?? 'N/A').toString();
    const serviceRequestIdRaw = (data.serviceRequestId ?? '').toString();
    const containerIdRaw = (data.containerId ?? '').toString();
    const patientName = (data.patientName ?? '').toString();
    const serviceRequestName = (data.serviceRequestName ?? '').toString();
    const specimenType = (data.specimenType ?? '').toString();
    const receivedTime = (data.receivedTime ?? '').toString();

    let patIdFormatted = patientPrimaryIdRaw;
    if (patientPrimaryIdRaw && patientPrimaryIdRaw !== 'N/A') {
      try { patIdFormatted = await firstValueFrom(this.refDisplay.ensureIdentifier('Patient/' + patientPrimaryIdRaw)); } catch (e) {}
    }

    let srIdFormatted = serviceRequestIdRaw;
    if (serviceRequestIdRaw) {
      try { srIdFormatted = await firstValueFrom(this.refDisplay.ensureIdentifier('ServiceRequest/' + serviceRequestIdRaw)); } catch (e) {}
    }

    let specIdFormatted = containerIdRaw;
    if (containerIdRaw) {
      try { specIdFormatted = await firstValueFrom(this.refDisplay.ensureIdentifier('Specimen/' + containerIdRaw)); } catch (e) {}
    }

    const qrData = JSON.stringify({
      p: patIdFormatted,
      pn: patientName,
      r: srIdFormatted,
      rn: serviceRequestName,
      s: specIdFormatted,
      st: specimenType,
      t: receivedTime
    });

    const margin = this.mmToPt(1); // 1mm margin
    const qrSize = this.mmToPt(18); // 18mm QR Code, safely fits within 25.4mm height

    const content: any[] = [
      {
        columns: [
          {
            width: '*',
            stack: [
              { text: patientName || patIdFormatted || 'N/A', style: 'headline', margin: [0, 0, 0, 1] },
              { text: `PID: ${patIdFormatted}`, style: 'muted' },
              { text: `Req: ${srIdFormatted}`, style: 'muted' },
              { text: `Req Name: ${serviceRequestName || 'N/A'}`, style: 'muted' },
              { text: `Specimen: ${specimenType || 'N/A'}`, style: 'muted' },
              { text: `Specimen ID: ${specIdFormatted}`, style: 'muted' }
            ]
          },
          {
            width: qrSize,
            stack: [
              {
                qr: qrData,
                fit: qrSize,
                alignment: 'right',
                margin: [0, 0, 0, 1]
              },
              {
                text: receivedTime || 'N/A',
                style: 'muted',
                alignment: 'center'
              }
            ]
          }
        ],
        columnGap: this.mmToPt(1)
      }
    ];

    const docDefinition: any = {
      pageSize: {
        width: this.mmToPt(50.8), // 2 inches
        height: this.mmToPt(25.4) // 1 inch
      },
      pageMargins: [margin, margin, margin, margin],
      content,
      styles: {
        headline: { fontSize: 8, bold: true },
        muted: { fontSize: 6.5, lineHeight: 1.1 }
      },
      defaultStyle: { fontSize: 6.5 }
    };

    this.pdf.print(docDefinition);
  }
}
